import React, { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import PostAddIcon from '@mui/icons-material/PostAdd';
import { Note } from '../types/notes';
import { useNoteEditorModel } from '../hooks/useNoteEditorModel';
import { useNoteMutations } from '../hooks/data/useNoteMutations';
import { useNoteImageUpload } from '../hooks/data/useNoteImageUpload';
import { useCategoryContext } from '../contexts/CategoryContext';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useCacheStore } from '../hooks/data/useCacheStore';
import { useWebAuthn } from '../contexts/WebAuthnContext';
import RichTextEditor from './RichTextEditor';
import NoteStatusIndicator from './NoteStatusIndicator';
import { ConfirmDialog } from './ConfirmDialog';
import { CategoryPicker } from './CategoryPicker';
import TemplatePicker from './TemplatePicker';
import { stripHtml } from '../utils/bookmarkUtils';

interface NoteEditorBodyProps {
  note: Note;
}

/**
 * Full-body note editor: editable title + status dot + actions menu, with the
 * full RichTextEditor filling the remaining height. Autosaves via the model.
 */
const NoteEditorBody: React.FC<NoteEditorBodyProps> = ({ note }) => {
  const {
    title,
    content,
    status,
    pendingDraft,
    externalContent,
    setTitle,
    setContent,
    restoreDraft,
    discardDraft,
  } = useNoteEditorModel(note);

  const { moveNote, softDeleteNote, addNote } = useNoteMutations();
  const { uploadImage } = useNoteImageUpload();
  const {
    selectNoteCategory,
    selectedNoteCategory,
    selectNote,
    pendingNoteSearch,
  } = useCategoryContext();
  const { isOnline } = useOnlineStatus();
  const cacheStore = useCacheStore();
  const { setSnackbar } = useWebAuthn();

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showMovePicker, setShowMovePicker] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  // Insert a template: replace when the note is effectively empty, else append.
  const handleInsertTemplate = React.useCallback(
    (html: string) => {
      const isEmpty = stripHtml(content).trim().length === 0;
      setContent(isEmpty ? html : `${content}${html}`);
    },
    [content, setContent],
  );

  const noteCategories = useMemo(
    () => cacheStore.getNoteCategories() ?? [],
    [cacheStore],
  );

  const handleImageUpload = React.useCallback(
    (file: File) => uploadImage(file, note.note_id),
    [uploadImage, note.note_id],
  );

  const handleDuplicate = async () => {
    const result = await addNote({
      note_title: `${title} (copy)`,
      note_content: content,
      category_id: note.category_id,
    });
    if (result.success && result.note) {
      selectNote(result.note);
    } else {
      setSnackbar({
        open: true,
        message: result.error || 'Failed to duplicate note.',
        severity: 'error',
      });
    }
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    const result = await softDeleteNote(note.note_id);
    if (result.success) {
      selectNoteCategory(selectedNoteCategory ?? null);
    } else {
      setSnackbar({
        open: true,
        message: result.error || 'Failed to delete note.',
        severity: 'error',
      });
    }
  };

  const handleMoveSelect = async (newCategoryId: string | null) => {
    setShowMovePicker(false);
    const result = await moveNote(note.note_id, newCategoryId);
    if (!result.success) {
      setSnackbar({
        open: true,
        message: result.error || 'Failed to move note.',
        severity: 'error',
      });
    }
  };

  return (
    <Box
      sx={{
        // Absolute-fill so the editor is bounded regardless of the parent being
        // an auto-overflow scroller; the inner editor region owns the scroll.
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      {/* Header: back + title + status + actions */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          pt: 1,
          pb: 0.5,
          flexShrink: 0,
        }}
      >
        <TextField
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          variant="standard"
          placeholder="Untitled note"
          inputProps={{ maxLength: 255, 'aria-label': 'Note title' }}
          sx={{
            flex: 1,
            '& input': { fontSize: '1.25rem', fontWeight: 600 },
          }}
        />
        <NoteStatusIndicator status={status} />
      </Box>

      {/* Draft recovery prompt */}
      {pendingDraft && (
        <Box
          sx={{
            mx: 2,
            mb: 1,
            p: 1,
            border: 1,
            borderColor: 'warning.main',
            borderRadius: 1,
            bgcolor: 'warning.50',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 160, fontSize: '0.875rem' }}>
            You have unsaved local changes to this note.
          </Box>
          <Button size="small" variant="contained" onClick={restoreDraft}>
            Restore
          </Button>
          <Button size="small" onClick={discardDraft}>
            Discard
          </Button>
        </Box>
      )}

      {/* Editor fills remaining height; RichTextEditor (fillHeight) owns scroll */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          px: 2,
          pb: 2,
          '& > div': { flex: 1, minHeight: 0 },
        }}
      >
        <RichTextEditor
          key={note.note_id}
          initialContent={content}
          externalContent={externalContent}
          onChange={setContent}
          preset="full"
          onImageUpload={handleImageUpload}
          fillHeight
          initialSearchTerm={pendingNoteSearch ?? undefined}
          fileMenuItems={(closeMenu) => [
            <MenuItem
              key="duplicate"
              onClick={() => {
                closeMenu();
                handleDuplicate();
              }}
              disabled={!isOnline}
            >
              <ListItemText>Duplicate</ListItemText>
            </MenuItem>,
            <MenuItem
              key="move"
              onClick={() => {
                closeMenu();
                setShowMovePicker(true);
              }}
              disabled={!isOnline}
            >
              <ListItemText>Move…</ListItemText>
            </MenuItem>,
            <MenuItem
              key="delete"
              onClick={() => {
                closeMenu();
                setConfirmDelete(true);
              }}
              disabled={!isOnline}
              sx={{ color: 'error.main' }}
            >
              <ListItemText>Delete</ListItemText>
            </MenuItem>,
          ]}
          insertMenuItems={(closeMenu) => (
            <MenuItem
              key="insert-template"
              onClick={() => {
                closeMenu();
                setShowTemplatePicker(true);
              }}
            >
              <ListItemIcon>
                <PostAddIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Insert Template</ListItemText>
            </MenuItem>
          )}
        />
      </Box>

      <ConfirmDialog
        open={confirmDelete}
        title="Move note to Trash?"
        message={`"${title}" will be moved to Trash. You can restore it later.`}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      <CategoryPicker
        open={showMovePicker}
        title="Move Note"
        categories={noteCategories}
        disabledIds={new Set()}
        currentLocationId={note.category_id}
        onSelect={handleMoveSelect}
        onCancel={() => setShowMovePicker(false)}
      />

      <TemplatePicker
        open={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onInsert={handleInsertTemplate}
      />
    </Box>
  );
};

export default NoteEditorBody;
